/**
 * ConfirmDialog Component
 *
 * Modal dialog for confirming destructive or important actions.
 *
 * GAP-014: Accessibility-compliant with proper focus trap
 * - Uses Radix UI Dialog for built-in focus management
 * - Traps focus within dialog when open
 * - Returns focus to trigger when closed
 */

import type { ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

import { cn } from '../../utils/cn';
import { Button } from '../Button';

export type ConfirmDialogVariant = 'danger' | 'warning' | 'info' | 'default';

export interface ConfirmDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Called when the dialog should close */
  onClose: () => void;
  /** Called when the confirm button is clicked */
  onConfirm: () => void;
  /** Dialog title */
  title: string;
  /** Dialog description/message */
  description: string;
  /** Label for confirm button */
  confirmLabel?: string;
  /** Label for cancel button */
  cancelLabel?: string;
  /** Visual variant of the dialog */
  variant?: ConfirmDialogVariant;
  /** Whether the confirm action is loading */
  loading?: boolean;
  /** Whether the confirm button is disabled */
  disabled?: boolean;
  /** Custom icon to display */
  icon?: ReactNode;
  /** Additional content between description and buttons */
  children?: ReactNode;
}

const variantConfig = {
  danger: {
    icon: ExclamationTriangleIcon,
    iconColor: 'text-mv-red-600',
    iconBg: 'bg-red-50',
    buttonVariant: 'danger' as const,
  },
  warning: {
    icon: ExclamationCircleIcon,
    iconColor: 'text-mv-amber-500',
    iconBg: 'bg-amber-50',
    buttonVariant: 'primary' as const,
  },
  info: {
    icon: InformationCircleIcon,
    iconColor: 'text-mv-blue-600',
    iconBg: 'bg-mv-blue-50',
    buttonVariant: 'primary' as const,
  },
  default: {
    icon: QuestionMarkCircleIcon,
    iconColor: 'text-mv-blue-600',
    iconBg: 'bg-mv-blue-50',
    buttonVariant: 'primary' as const,
  },
};

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Bevestigen',
  cancelLabel = 'Annuleren',
  variant = 'default',
  loading = false,
  disabled = false,
  icon,
  children,
}: ConfirmDialogProps) {
  const config = variantConfig[variant];
  const IconComponent = config.icon;

  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-fade-in" />
        {/* GAP-014: Focus trap handled by Radix Dialog */}
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2',
            'rounded-lg bg-white p-6 shadow-lg',
            'data-[state=open]:animate-slide-up',
            // GAP-014: Visible focus ring for keyboard navigation
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-mv-blue-500 focus-visible:ring-offset-2'
          )}
          aria-modal="true"
        >
          <div className="flex gap-4">
            {/* Icon */}
            <div
              className={cn(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                config.iconBg
              )}
            >
              {icon || <IconComponent className={cn('h-5 w-5', config.iconColor)} />}
            </div>

            {/* Content */}
            <div className="flex-1">
              <Dialog.Title className="text-lg font-semibold text-mv-gray-900">
                {title}
              </Dialog.Title>
              <Dialog.Description className="mt-2 text-sm text-mv-gray-600">
                {description}
              </Dialog.Description>
              {children && <div className="mt-4">{children}</div>}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={loading}
            >
              {cancelLabel}
            </Button>
            <Button
              variant={config.buttonVariant}
              onClick={handleConfirm}
              isLoading={loading}
              disabled={disabled}
            >
              {confirmLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// Hook for managing confirm dialog state
import { useState, useCallback } from 'react';

export interface UseConfirmDialogOptions {
  /** Callback when confirmed - if not provided, confirm() returns a Promise<boolean> */
  onConfirm?: () => void | Promise<void>;
  title: string;
  /** Description/message text */
  description?: string;
  /** Alias for description */
  message?: string;
  confirmLabel?: string;
  /** Alias for confirmLabel */
  confirmText?: string;
  /** Label for cancel button */
  cancelLabel?: string;
  /** Alias for cancelLabel */
  cancelText?: string;
  variant?: ConfirmDialogVariant;
  /** Alias for variant */
  type?: ConfirmDialogVariant;
}

export function useConfirmDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<UseConfirmDialogOptions | null>(null);
  const [resolvePromise, setResolvePromise] = useState<((value: boolean) => void) | null>(null);

  const confirm = useCallback((opts: UseConfirmDialogOptions): Promise<boolean> | void => {
    setOptions(opts);
    setIsOpen(true);

    // If no onConfirm callback, return a Promise
    if (!opts.onConfirm) {
      return new Promise<boolean>((resolve) => {
        setResolvePromise(() => resolve);
      });
    }
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!options) return;

    try {
      setIsLoading(true);
      if (options.onConfirm) {
        await options.onConfirm();
      }
      if (resolvePromise) {
        resolvePromise(true);
        setResolvePromise(null);
      }
      setIsOpen(false);
    } finally {
      setIsLoading(false);
    }
  }, [options, resolvePromise]);

  const handleClose = useCallback(() => {
    if (!isLoading) {
      if (resolvePromise) {
        resolvePromise(false);
        setResolvePromise(null);
      }
      setIsOpen(false);
    }
  }, [isLoading, resolvePromise]);

  const dialogProps = {
    open: isOpen,
    onClose: handleClose,
    onConfirm: handleConfirm,
    title: options?.title ?? '',
    description: options?.description ?? options?.message ?? '',
    confirmLabel: options?.confirmLabel ?? options?.confirmText,
    cancelLabel: options?.cancelLabel ?? options?.cancelText,
    variant: options?.variant ?? options?.type,
    loading: isLoading,
  };

  return { confirm, dialogProps, isOpen, isLoading };
}
